import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import identity from '../data/siteIdentity.json';
import './AboutPage.css';

// /about — who is behind UBOTIKA: the founder, how we work, and every real way to
// reach us. Every fact here is sourced from siteIdentity.json or an existing page;
// do not add team members, client counts, testimonials or quotes that aren't real.

const TITLE = 'About UBOTIKA | AI Studio in Los Angeles';
const DESCRIPTION = 'Meet UBOTIKA, a Los Angeles AI studio founded by Edoe Balint: what we build, how we work, our guarantee, and real ways to reach us by phone, email or at WeWork LA.';
const LEGAL_NAME = 'U Botika LLC';
const FOUNDER = 'Edoe Balint';

// "+1-310-912-3548" -> { display: "(310) 912-3548", tel: "tel:+13109123548" }
function phone(e164ish) {
  const digits = e164ish.replace(/\D/g, '').replace(/^1/, '');
  return {
    display: `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`,
    tel: `tel:+1${digits}`,
  };
}
const BUSINESS_PHONE = phone(identity.telephone);
const DEMO_PHONE = phone(identity.demoTelephone);

const SERVICES = [
  { name: 'AI phone receptionist', body: 'Tessa answers every call 24/7, in English and Spanish, and books appointments.', to: '/ai-receptionist-los-angeles' },
  { name: 'Websites', body: 'A professional website at a fixed price, live in two weeks.', to: '/lp/websites' },
  { name: 'SEO and AI search', body: 'We fix what keeps customers from finding you on Google and in AI search.' },
  { name: 'Promo videos', body: 'Short promo videos for your website and social media.' },
  { name: 'Custom web apps', body: 'Tailor-made tools, portals and dashboards built around how your business works.', to: '/web-apps' },
  { name: 'Live support agents', body: 'An agent that talks customers through your site in real time, by voice or chat.' },
  { name: 'Cold email outreach', body: 'We find your ideal customers, write the emails and run the campaign.' },
  { name: 'Private AI for law firms', body: 'A private AI model on hardware at your firm, for files that can’t go to the cloud.', to: '/localllm' },
];

const PRINCIPLES = [
  {
    title: 'If we can’t save you money, you don’t pay.',
    body: 'That is our guarantee. We start from what a missed call, a slow follow-up or a manual task costs you today.',
  },
  {
    title: 'Prices you can read before you call',
    body: 'AI receptionist plans are published on our pricing page, starting at $500 a month with minutes included.',
    link: { to: '/pricing', label: 'See pricing' },
  },
  {
    title: 'A fixed price, agreed up front',
    body: 'Custom work is quoted as a fixed price per phase. You approve the number before any work starts.',
  },
  {
    title: 'You own what we build',
    body: 'The source code and the data belong to you, not to us.',
  },
  {
    title: 'Try it on your own calls first',
    body: 'Run the AI receptionist on your real calls free for two weeks and listen to the recordings before you decide.',
  },
  {
    title: 'An NDA before anything confidential',
    body: 'We sign one before we see a system or a file that isn’t public.',
  },
];

const PROFILES = [
  { label: 'LinkedIn', match: 'linkedin.com' },
  { label: 'YouTube', match: 'youtube.com' },
  { label: 'X', match: 'x.com' },
  { label: 'Google Maps', match: 'maps.google.com' },
]
  .map((p) => ({ ...p, href: identity.sameAs.find((url) => url.includes(p.match)) }))
  .filter((p) => p.href);

const ORG_ID = `${SITE_URL}/#organization`;
const PERSON_ID = `${SITE_URL}/about#edoe-balint`;

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${SITE_URL}/about#aboutpage`,
    url: `${SITE_URL}/about`,
    name: TITLE,
    description: DESCRIPTION,
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': ORG_ID },
    mainEntity: { '@id': PERSON_ID },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': PERSON_ID,
    name: FOUNDER,
    jobTitle: 'Founder',
    url: `${SITE_URL}/about`,
    worksFor: {
      '@type': 'LocalBusiness',
      '@id': ORG_ID,
      name: identity.name,
      legalName: LEGAL_NAME,
      url: SITE_URL,
    },
    workLocation: {
      '@type': 'Place',
      name: 'WeWork, Los Angeles',
      address: {
        '@type': 'PostalAddress',
        addressLocality: identity.addressLocality,
        addressRegion: identity.addressRegion,
        addressCountry: identity.addressCountry,
      },
    },
    knowsAbout: ['AI Phone Receptionists', 'AI Agents', 'Web Development', 'Custom Web Apps', 'SEO', 'Private Local AI'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': ORG_ID,
    name: identity.name,
    legalName: LEGAL_NAME,
    url: SITE_URL,
    telephone: identity.telephone,
    email: identity.email,
    founder: { '@id': PERSON_ID },
    address: {
      '@type': 'PostalAddress',
      addressLocality: identity.addressLocality,
      addressRegion: identity.addressRegion,
      addressCountry: identity.addressCountry,
    },
    sameAs: identity.sameAs,
  },
];

function AboutPage() {
  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path="/about" schema={schema} />
      <main id="main-content" className="ab-page">
        <section className="ab-hero">
          <p className="ab-eyebrow">About UBOTIKA</p>
          <h1>A Los Angeles AI studio, with a real person behind it.</h1>
          <p className="ab-lede">
            UBOTIKA builds AI phone receptionists, websites and tailor-made apps for local
            businesses. The studio was founded by {FOUNDER} and works out of WeWork in Los Angeles.
          </p>
          <dl className="ab-facts">
            <div><dt>Founder</dt><dd>{FOUNDER}</dd></div>
            <div><dt>Based in</dt><dd>{identity.addressLocality}, {identity.addressRegion}</dd></div>
            <div><dt>Legal name</dt><dd>{LEGAL_NAME}</dd></div>
            <div><dt>Phone</dt><dd><a href={BUSINESS_PHONE.tel}>{BUSINESS_PHONE.display}</a></dd></div>
          </dl>
        </section>

        <section className="ab-section" aria-labelledby="ab-who">
          <div className="ab-split">
            <div>
              <p className="ab-eyebrow">Who we are</p>
              <h2 id="ab-who">We take the repetitive work off your plate.</h2>
            </div>
            <div className="ab-prose">
              <p>
                Most local businesses don&apos;t need another app to learn. They need the phone
                answered, the follow-up sent and the paperwork filled in, so the owner can get back
                to the work only they can do.
              </p>
              <p>
                That is what UBOTIKA is for. We build it, we set it up and we keep it running. Our
                AI receptionist, Tessa, answers calls around the clock and books appointments. Around
                her we build the website, the search presence and the custom tools a business needs.
              </p>
              <p>
                You can hear Tessa right now: call{' '}
                <a href={DEMO_PHONE.tel}>{DEMO_PHONE.display}</a>. That line is our AI demo, not a
                person.
              </p>
            </div>
          </div>
        </section>

        <section className="ab-section" aria-labelledby="ab-what">
          <p className="ab-eyebrow">What we build</p>
          <h2 id="ab-what">One studio, eight services.</h2>
          <ul className="ab-services">
            {SERVICES.map((s) => (
              <li key={s.name}>
                <h3>{s.to ? <Link to={s.to}>{s.name}</Link> : s.name}</h3>
                <p>{s.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="ab-section" aria-labelledby="ab-founder">
          {/* To add a photo later, put one <img className="ab-founder-img" ... /> as the
              first child of .ab-founder; the layout switches to two columns on its own. */}
          <div className="ab-founder">
            <div className="ab-founder-body">
              <p className="ab-eyebrow">The founder</p>
              <h2 id="ab-founder">{FOUNDER}</h2>
              <p className="ab-founder-role">Founder, {identity.name} · {identity.addressLocality}, {identity.addressRegion}</p>
              <div className="ab-prose">
                <p>
                  Edoe founded UBOTIKA in Los Angeles. The studio exists for one reason: to take the
                  repetitive work off a business owner&apos;s plate, so the phone gets answered, the
                  follow-up goes out and the owner can focus on running the business.
                </p>
                <p>
                  Edoe works out of WeWork in Los Angeles and stands behind the studio&apos;s
                  guarantee: if we can&apos;t save you money, you don&apos;t pay.
                </p>
              </div>
              <p className="ab-founder-reach">
                Reach the studio at <a href={`mailto:${identity.email}`}>{identity.email}</a> or{' '}
                <a href={BUSINESS_PHONE.tel}>{BUSINESS_PHONE.display}</a>.
              </p>
            </div>
          </div>
        </section>

        <section className="ab-section" aria-labelledby="ab-how">
          <p className="ab-eyebrow">How we work</p>
          <h2 id="ab-how">Plain promises, written down.</h2>
          <ol className="ab-principles">
            {PRINCIPLES.map((p, i) => (
              <li key={p.title}>
                <span className="ab-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
                {p.link && <Link className="ab-text-link" to={p.link.to}>{p.link.label} →</Link>}
              </li>
            ))}
          </ol>
        </section>

        <section className="ab-section ab-contact" aria-labelledby="ab-contact">
          <p className="ab-eyebrow">Contact</p>
          <h2 id="ab-contact">Real ways to reach us.</h2>
          <div className="ab-contact-grid">
            <div className="ab-contact-card">
              <h3>Business line</h3>
              <a className="ab-contact-value" href={BUSINESS_PHONE.tel}>{BUSINESS_PHONE.display}</a>
              <p>For new projects and questions about what we build.</p>
            </div>
            <div className="ab-contact-card">
              <h3>Email</h3>
              <a className="ab-contact-value" href={`mailto:${identity.email}`}>{identity.email}</a>
              <p>Write to us about anything, from a quick question to a full project.</p>
            </div>
            <div className="ab-contact-card">
              <h3>AI receptionist demo</h3>
              <a className="ab-contact-value" href={DEMO_PHONE.tel}>{DEMO_PHONE.display}</a>
              <p>Answered by Tessa, our AI. Call it to hear how your customers would be greeted.</p>
            </div>
            <div className="ab-contact-card">
              <h3>Where we are</h3>
              <p className="ab-contact-value">{identity.addressLocality}, {identity.addressRegion}</p>
              <p>We work out of WeWork in Los Angeles.</p>
            </div>
          </div>

          <div className="ab-actions">
            <Link className="cta-button ab-cta" to="/free-missed-lead-audit">Get a free missed-lead audit</Link>
            <Link className="ab-ghost" to={{ pathname: '/', hash: '#contact' }} state={{ scrollTo: 'contact' }}>
              Send us a message
            </Link>
          </div>

          {PROFILES.length > 0 && (
            <p className="ab-profiles">
              Also find {identity.name} on{' '}
              {PROFILES.map((p, i) => (
                <React.Fragment key={p.label}>
                  {i > 0 && (i === PROFILES.length - 1 ? ' and ' : ', ')}
                  <a href={p.href} target="_blank" rel="noopener noreferrer">{p.label}</a>
                </React.Fragment>
              ))}
              .
            </p>
          )}
        </section>
        <Footer />
      </main>
    </>
  );
}

export default AboutPage;
